import React, { useRef, useState, useEffect } from "react";
import { Box } from "@mui/material";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import CustomStepper from "../../../../../../shared/components/stepper/steppers";
import { InnerButton } from "../../../../../../shared/components/buttons/floating-button-container";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import CustomTable from "../../../../../../shared/components/table/custom-table";
import { ActionsMenu } from "../../../../../../shared/components/table/custom-actions";
import { MRT_ColumnDef } from "material-react-table";
import ActionsModalPopup, { ActionButtonConfig } from "../../../../../../shared/components/modals/action-popup";
import AlertPopup from "../../../../../../shared/components/modals/alert-moadal";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import RenderDetailPanel from "./render-detail-panel";
import IncomingTcInfo from "./incoming-details/incoming-tc-info";
import ProductNetWeight from "./incoming-details/product-net-weight";
import IncomingTcStandard, { initIncomingTcStandardMap } from "./incoming-details/incoming-tc-standard";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { 
  fetchIncomingTcDetailsList, 
  fetchIncomingTcDetail,
  deleteIncomingTcDetail,
  IncomingTcEntry 
} from "./incoming-details-apis";
import { IncomingTCInformation, IncomingProductNetWeightInfo } from "../../../tc-models/tc-req-dto/incoming-details.req.dto";
import { ProductShipmentResponse } from "../certified-products/certified-products.model";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";

const initIncomingTcInfo: IncomingTCInformation = {
  tcAssociatedProductArticleNo: "",
  tcIncomingTCNo: "",
  tcIncomingATCNo:"",
  tcIncomingRMDFNo: "",
  tcIncomingTCStandard: [],
  tcIncomingProductNo: [],
  tcIncomingTcId: "",
  tcIncomingAtcId: "",
  tcIncomingLedgerTransactionId: "",
};

const initProductNetWeightInfo: IncomingProductNetWeightInfo = {
  netWeights: {},
  rawMaterials: {},
};

interface IncomingTcDetailsProps {
  onNextTab: () => Promise<void>;
}

const IncomingTcDetails: React.FC<IncomingTcDetailsProps> = ({ onNextTab }) => {
  const { notify } = useNotification();
  const { executeGet, executePost, executeDelete, setLoadingState } = useApi();
  const { TE_ID, formtype } = useParams<string>();
  const navigate = useNavigate();

  const [tableData, setTableData] = useState<IncomingTcEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteRowData, setDeleteRowData] = useState<IncomingTcEntry | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const incomingTcRef = useRef<any>(null);
  const incomingTcStandardRef = useRef<any>(null);
  const productWeightRef = useRef<any>(null);

  const [stepData, setStepData] = useState({
    incomingTcInfo: initIncomingTcInfo,
    incomingProductNetWeight: initProductNetWeightInfo,
    incomingTcStandard: initIncomingTcStandardMap as { [key: string]: string[] },
  });

  const normalizeStandards = (arr: any): string[] => {
    if (!Array.isArray(arr)) return [];
    
    let flattened: any[] = [];
    if (typeof (Array.prototype as any).flat === "function") {
      flattened = (arr as any).flat(Infinity).filter((v: any) => v !== null && v !== undefined);
    } else {
      flattened = (arr as any).reduce((acc: any[], cur: any) => 
        acc.concat(Array.isArray(cur) ? cur : [cur]), []
      ).filter((v: any) => v !== null && v !== undefined);
    }
    
    // Split concatenated standards (e.g., "GRSRCS" -> ["GRS", "RCS"])
    const result: string[] = [];
    flattened.forEach((item) => {
      const str = String(item).trim();
      if (!str) return;
      
      // Check if it's concatenated (common standards are 3-4 chars)
      if (str.length > 4 && !str.includes(',') && !str.includes(' ')) {
        // Try to split by known standard patterns
        const matches = str.match(/([A-Z]{2,4})/g);
        if (matches) {
          result.push(...matches);
        } else {
          result.push(str);
        }
      } else {
        result.push(str);
      }
    });
    
    // Remove duplicates
    return [...new Set(result)];
  };

  // const normalizeStandards = (arr: any[]): string[] => {
  //   if (!Array.isArray(arr)) return [];
  //   return arr.flat(Infinity).filter(v => v != null).map(String);
  // };

  const [incomingTcId, setIncomingTcId] = useState<string | null>(null);
  const [productData, setProductData] = useState<any[]>([]);
  const [oldProductId, setOldProductId] = useState<string | null>(null);
  const [originalArticleNo, setOriginalArticleNo] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const hasCreateTc: boolean = formtype === "create-tc";
  const hasSubmitTc: boolean = formtype === "submited-tc";
  const hasRejectedTc: boolean = formtype === "rejected-tc";

  useEffect(() => {
    if (TE_ID) {
      loadIncomingTcDetails();
      loadProductShipmentData();
    }
  }, [TE_ID, pagination?.pageIndex]);

  // When tcIncomingProductNo changes, clear standards map to prevent old product standards from persisting
  useEffect(() => {
    const selectedProducts = stepData.incomingTcInfo.tcIncomingProductNo;
    if (Array.isArray(selectedProducts) && selectedProducts.length === 0) {
      // Clear the standards map when no products are selected
      setStepData((prev) => ({
        ...prev,
        incomingTcStandard: {},
      }));
    }
  }, [JSON.stringify(stepData.incomingTcInfo.tcIncomingProductNo)]);

  const loadProductShipmentData = async () => {
    try {
      const response = await executeGet(TCAppConfig.GET_LIST_PRODUCT_INFO_V4, { tcId: TE_ID });
      
      // Handle response - it might be a string, array, or undefined
      let responseArray: any[] = [];
      
      if (response?.data?.response) {
        // If response is a string, parse it
        if (typeof response.data.response === 'string') {
          try {
            responseArray = JSON.parse(response.data.response);
          } catch (parseError) {
            console.error("Failed to parse response string:", parseError);
            responseArray = [];
          }
        } 
        // If response is already an array, use it
        else if (Array.isArray(response.data.response)) {
          responseArray = response.data.response;
        }
        // Otherwise, default to empty array
        else {
          responseArray = [];
        }
      }
      
      setProductData(responseArray);
    } catch (error) {
      console.error("Error fetching shipment data:", error);
    }
  };

  const loadIncomingTcDetails = async () => {
    if (!TE_ID) return;
    try {
      setIsLoading(true);
      const offset = pagination.pageIndex * pagination.pageSize;
      const data = await fetchIncomingTcDetailsList(
        executeGet,
        TE_ID,
        pagination.pageSize,
        offset
      );
      setTableData(data?.entries);
      setRowCount(data?.totalCount);
    } catch (error) {
      console.error("Error loading incoming TC details:", error);
      // notify("Failed to load incoming TC details", "error");
      setTableData([]);
      setRowCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const getProductShipmentDataByArticle = (list: ProductShipmentResponse[], articleNo: string) => {
    return list.find((item) => item.tcProductArticleNo === articleNo);
  };

  const resetDrawerState = () => {
    setStepData({
      incomingTcInfo: initIncomingTcInfo,
      incomingProductNetWeight: initProductNetWeightInfo,
      incomingTcStandard: initIncomingTcStandardMap,
    });
    setIncomingTcId(null);
    setOldProductId(null);
    setOriginalArticleNo(null);
    setIsCopy(false);
    setIsEdit(false);
    setActiveStep(0);
  };

  const openDrawer = (copy = false, data?: IncomingTcEntry) => {
    if (data) {
      const isSeller = (data.incomingTcInfo as any)?.isFlagSeller === 'Yes';
      
      const standardsFromPayload = data.productNetWeight?.standards || {};
      const normalizedStandardsMap = Object.keys(standardsFromPayload || {}).length > 0
        ? Object.entries(standardsFromPayload).reduce<Record<string, string[]>>((acc, [article, standards]) => {
            acc[article] = normalizeStandards(standards);
            return acc;
          }, {})
        : {};

      const fallbackStandardsMap = (() => {
        const map: { [key: string]: string[] } = {};
        const standards = normalizeStandards(data.incomingTcInfo?.tcIncomingTCStandard || []);
        const articles = normalizeStandards(data.incomingTcInfo?.tcIncomingProductNo || []);
        articles.forEach((a) => {
          map[a] = [...standards];
        });
        return map;
      })();

      setStepData({
        incomingTcInfo: {
          ...data.incomingTcInfo,
          tcIncomingTCStandard: normalizeStandards(data.incomingTcInfo?.tcIncomingTCStandard),
          tcIncomingProductNo: isSeller ? ["na"] : normalizeStandards(data.incomingTcInfo?.tcIncomingProductNo || []),
          incomingTcLicensingCode: isSeller ? "na" : (data.incomingTcInfo?.incomingTcLicensingCode || "").replace(/^CB-/, ""),
          isFlagSeller: isSeller ? "Yes" : "No",
        },
        incomingProductNetWeight: data.productNetWeight,
        incomingTcStandard: isSeller
          ? {}
          : (Object.keys(normalizedStandardsMap).length > 0 ? normalizedStandardsMap : fallbackStandardsMap),
      });
      setIncomingTcId(data.incomingTcId || null);
      setOldProductId(data.productId || null);
      setOriginalArticleNo(data.incomingTcInfo.tcAssociatedProductArticleNo || null);
    } else {
      resetDrawerState();
    }
    setIsCopy(copy);
    setIsEdit(!copy && Boolean(data));
    setDrawerOpen(true);
  };

  const handleAdd = () => openDrawer(false);

  const handleEdit = async (row: any) => {
    const incomingTcId = row.original.incomingTcId || row.original.incoming_tc_id;
    const productId = row.original.productId || row.original.product_id;
    try {
      const entry = await fetchIncomingTcDetail(executeGet, TE_ID!, incomingTcId, productId);
      if (entry) {
        openDrawer(false, entry);
      } else if (row.original.incomingTcProductInformation?.[0] || row.original.inputTcInformation?.[0]) {
        const info = row.original.incomingTcProductInformation?.[0] || row.original.inputTcInformation?.[0];
        const netWeights: { [key: string]: number } = {};
        const rawMaterials: { [key: string]: any[] } = {};
        const standardsMap: { [key: string]: string[] } = {};

        const weightInfos = info.incomingTcProductNetWeightUsedInformation || info.inputTcProductNetWeightUsedInformation || [];
        (Array.isArray(weightInfos) ? weightInfos : []).forEach((item: any) => {
          const articleNo = item.tcProductIncomingArticleNo || item.incomingArticleNo || item.articleNo || "";
          if (!articleNo) return;

          const netWeightValue = Number(
            item.tcProductIncomingArticleNetWeightUsed ??
            item.incomingTcProductNetWeightUsed ??
            0
          );
          if (netWeightValue > 0) {
            netWeights[articleNo] = netWeightValue;
          }

          const rawList = item.tcProductIncomingRawMaterialInformation || item.incomingTcRawMaterialInformation || [];
          if (Array.isArray(rawList) && rawList.length > 0) {
            rawMaterials[articleNo] = rawList.map((rm: any) => ({
              tcProductCategoryCode: rm.tcProductCategoryCode,
              tcProductDetailCode: rm.tcProductDetailCode,
              rawMaterialCode: rm.rawMaterialCode,
              rawMaterialPercentage: rm.rawMaterialPercentage,
            }));
          }

          const standardsForArticle = normalizeStandards(
            item.tcProductIncomingTcStandard || item.incomingProductStandard || []
          );
          if (standardsForArticle.length > 0) {
            standardsMap[articleNo] = standardsForArticle;
          }
        });

        const incomingProducts = Array.isArray(info.incomingProductArticleNo)
          ? info.incomingProductArticleNo
          : Object.keys(netWeights);

        const isSeller = (info.isSellerFarm === 'Yes' || info.isSeller === 'Yes' || info.isFlagSeller === 'Yes');
        
        openDrawer(false, {
          id: row.original.id || 0,
          incomingTcId: incomingTcId || info.incomingTcNoId || row.original.incoming_tc_id || undefined,
          productId: productId || row.original.product_id,
          incomingTcInfo: {
            tcAssociatedProductArticleNo: info.tcAssociatedProductArticleNo || "",
            tcIncomingTCNo: info.tcProductIncomingTcNo || info.incomingTcNo || "",
            tcIncomingATCNo: "",
            tcIncomingRMDFNo: "",
            incomingTcLicensingCode: (info.incomingTcLicensingCode || "").replace(/^CB-/, ""),
            tcIncomingTCStandard: normalizeStandards(
              info.incomingTcStandard || Object.values(standardsMap).flat()
            ),
            tcIncomingProductNo: isSeller ? ["na"] : incomingProducts,
            tcIncomingTcId: info.incomingTcNoId || "",
            tcIncomingAtcId: "",
            tcIncomingLedgerTransactionId: "",
            isFlagSeller: isSeller ? "Yes" : "No",
          },
          productNetWeight: {
            netWeights,
            rawMaterials,
            standards: standardsMap,
          },
        } as IncomingTcEntry);
      } else {
        openDrawer(false, {
          id: row.original.id || 0,
          incomingTcId: incomingTcId,
          productId: productId || row.original.product_id,
          incomingTcInfo: {
            ...(row.original.incomingTcInfo || {}),
            tcAssociatedProductArticleNo: row.original.tcAssociatedProductArticleNo || row.original.tc_product_article_no || row.original.incomingTcInfo?.tcAssociatedProductArticleNo || "",
            tcIncomingTCNo: row.original.tcIncomingTCNo || row.original.tcIncomingATCNo || row.original.incomingTcInfo?.tcIncomingTCNo ||
            row.original.tcIncomingRMDFNo || row.original.incomingTcInfo?.tcIncomingRMDFNo || "",
            tcIncomingATCNo: row.original.tcIncomingATCNo || row.original.incomingTcInfo?.tcIncomingATCNo || "",
            tcIncomingRMDFNo: row.original.tcIncomingRMDFNo || row.original.incomingTcInfo?.tcIncomingRMDFNo || "",
            tcIncomingTCStandard: normalizeStandards(row.original.tcIncomingTCStandard || row.original.incomingTcInfo?.tcIncomingTCStandard || []),
            tcIncomingProductNo: normalizeStandards(row.original.tcIncomingProductNo || row.original.incomingTcInfo?.tcIncomingProductNo || []),
            incomingTcLicensingCode: (row.original.incomingTcInfo?.incomingTcLicensingCode || row.original.incomingTcInfo?.licensingCode || row.original.incomingTcInfo?.tcIncomingLicensingCode || "").replace(/^CB-/, ""),
            tcIncomingTcId: row.original.incomingTcInfo?.tcIncomingTcId || row.original.incomingTcId || "",
            tcIncomingAtcId: row.original.incomingTcInfo?.tcIncomingAtcId || "",
            tcIncomingLedgerTransactionId: row.original.incomingTcInfo?.tcIncomingLedgerTransactionId || "",
            isFlagSeller: (row.original.incomingTcInfo?.isFlagSeller || row.original.isFlagSeller || "No") === "Yes" ? "Yes" : "No",
          },
          productNetWeight: row.original.productNetWeight || { netWeights: {}, rawMaterials: {} },
        } as IncomingTcEntry);
      }
    } catch (error) {
      console.error("Error fetching edit data:", error);
      notify("Failed to load data for editing", "error");
    }
  };

  const handleCopy = async (row: any) => {
    const incomingTcId = row.original.incomingTcId || row.original.incoming_tc_id;
    const productId = row.original.productId || row.original.product_id;
    try {
      const entry = await fetchIncomingTcDetail(executeGet, TE_ID!, incomingTcId, productId);
      if (entry) {
        openDrawer(true, entry);
      } else {
        openDrawer(true, {
          id: row.original.id || 0,
          incomingTcId: undefined,
          productId: row.original.product_id || row.original.productId,
          incomingTcInfo: row.original.incomingTcInfo || {
            tcAssociatedProductArticleNo: row.original.tcAssociatedProductArticleNo || row.original.tc_product_article_no || "",
            tcIncomingTCNo: row.original.tcIncomingTCNo || row.original.tcIncomingATCNo || row.original.tcIncomingRMDFNo || "",
            tcIncomingATCNo: row.original.tcIncomingATCNo || "",
            tcIncomingRMDFNo: row.original.tcIncomingRMDFNo || "",
            tcIncomingTCStandard: row.original.tcIncomingTCStandard || [],
            tcIncomingProductNo: row.original.tcIncomingProductNo || [],
            incomingTcLicensingCode: (row.original.incomingTcInfo?.incomingTcLicensingCode || row.original.licensingCode || "").replace(/^CB-/, ""),
            tcIncomingTcId: "",
            tcIncomingAtcId: "",
            tcIncomingLedgerTransactionId: "",
            isFlagSeller: (row.original.incomingTcInfo?.isFlagSeller || row.original.isFlagSeller || "No") === "Yes" ? "Yes" : "No",
          },
          productNetWeight: { netWeights: {}, rawMaterials: {}, standards: {} },
        } as IncomingTcEntry);
      }
    } catch (error) {
      console.error("Error fetching copy data:", error);
      notify("Failed to load data for copying", "error");
    }
  };

  const handleDelete = (row: any) => {
    console.log("handleDelete - Full row data:", row.original);
    
    // Extract incomingTcId from multiple possible locations
    const incomingTcId = 
      row.original.incomingTcId || 
      row.original.incoming_tc_id || 
      null;
    
    console.log("handleDelete - Extracted incomingTcId:", incomingTcId);
    
    setDeleteId(incomingTcId);
    setDeleteRowData(row.original);
    setDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    console.log("handleConfirmDelete invoked", { deleteId, deleteRowData });
    if (!TE_ID) return;
  
    const row = deleteRowData ?? tableData.find((item) => item.incomingTcId === deleteId);
    if (!row) {
      console.warn("No row found for deletion", { deleteId });
      setDialogOpen(false);
      return;
    }
  
    console.log("handleConfirmDelete - Full row data:", row);
  
    const resolvedIncomingTcId = 
      row.incomingTcId 
      deleteId ||
      null;
  
    console.log("handleConfirmDelete - Resolved incomingTcId:", resolvedIncomingTcId);
  
    const incomingTcNo =
      row.incomingTcInfo?.tcIncomingTCNo || 
      row.incomingTcInfo?.tcIncomingATCNo ||
      row.incomingTcInfo?.tcIncomingRMDFNo ||
      (row as any).incomingTcNo ||
      (row as any).incoming_tc_no ||
      "";
    
    const productId = row.productId || (row as any).product_id || "";
    
    const productArticleNo =
      row.incomingTcInfo?.tcAssociatedProductArticleNo || 
      (row as any).tcAssociatedProductArticleNo ||
      (row as any).tc_product_article_no ||
      "";
  
    if (!resolvedIncomingTcId) {
      notify("Cannot delete: Incoming TC ID not found", "error");
      console.error("Missing incomingTcId for deletion");
      setDialogOpen(false);
      return;
    }
  
    try {
      setIsLoading(true);
      await deleteIncomingTcDetail(
        executeDelete,
        TE_ID,
        resolvedIncomingTcId,
        incomingTcNo,
        productId,
        productArticleNo
      );
      await loadIncomingTcDetails();
      notify("Deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting record:", error);
      notify("Failed to delete record", "error");
    } finally {
      setDialogOpen(false);
      setIsLoading(false);
      setDeleteId(null);
      setDeleteRowData(null);
    }
  };


const handleNext = async () => {
  const isSellerLocal = (stepData?.incomingTcInfo as any)?.isFlagSeller === 'Yes';
  const isRMDFLocal =
    !isSellerLocal &&
    !!(
      (stepData?.incomingTcInfo as any)?.tcIncomingRMDFNo &&
      (stepData?.incomingTcInfo as any)?.tcIncomingRMDFNo !== 'na'
    );

  const refToUse =
    activeStep === 0 ? incomingTcRef : activeStep === 1 ? incomingTcStandardRef : productWeightRef;

  // Special handling for Incoming TC Standard step:
  // Only enforce when the standards step actually exists (non-seller, non-RMDF flows)
  if (!isSellerLocal && !isRMDFLocal && activeStep === 1) {
    const standardsMap = stepData.incomingTcStandard || {};
    const selectedArticles = Array.isArray(stepData.incomingTcInfo.tcIncomingProductNo)
      ? stepData.incomingTcInfo.tcIncomingProductNo
      : [];

    const invalid = selectedArticles.filter(
      (article) =>
        !standardsMap[article] ||
        !Array.isArray(standardsMap[article]) ||
        standardsMap[article].length === 0
    );

    if (invalid.length > 0) {
      notify("At least one standard is required for each selected product article no", "error");
      return;
    }
  }

  const currentStepsLen = isSellerLocal ? 1 : isRMDFLocal ? 2 : steps.length;

  if (refToUse?.current && typeof refToUse.current.validateForm === "function") {
    const errors = await refToUse.current.validateForm();

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      const errorMessage =
        typeof firstError === "string"
          ? firstError
          : Array.isArray(firstError)
          ? firstError[0]
          : "Please fill required fields correctly";

      notify(errorMessage, "error");

      if (refToUse.current.setTouched) {
        const touchedFields = Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {} as any);
        refToUse.current.setTouched(touchedFields);
      }
      return;
    }

    await refToUse.current.submitForm();
    setActiveStep((prev) => Math.min(prev + 1, currentStepsLen - 1));
  } else {
    setActiveStep((prev) => Math.min(prev + 1, currentStepsLen - 1));
  }
};

  const handleNavigate = async () => {
    if(onNextTab){
      await onNextTab();
    }
    navigate(`/v4/tc/${formtype}/certified-standard-weight/${TE_ID}`);
  };

  // ========================================
  // HANDLE SAVE WITH 4 SCENARIOS
  // ========================================
  const handleFinish = async () => {
    if (!TE_ID) {
      notify("TE-ID missing", "error");
      return;
    }
    const isSeller = (stepData?.incomingTcInfo as any)?.isFlagSeller === 'Yes';

    // If seller flag is Yes, only require associated article and build 'na' payload
    if (isSeller) {
      const articleNo = stepData.incomingTcInfo.tcAssociatedProductArticleNo;
      if (!articleNo) {
        notify("Please select Associated Product Article Number", "error");
        return;
      }

      // Find shipment data for the selected article
      const shipment = getProductShipmentDataByArticle(productData, articleNo);
      if (!shipment) {
        notify("No shipment found for selected article", "error");
        return;
      }

      setLoadingState(true);

      const currentProductId = shipment.productId;

      // Check if this is edit mode (not copy and incomingTcId exists)
      const isNewIncomingTC = !incomingTcId || incomingTcId === "";
      const isEditMode = !isCopy && !isNewIncomingTC;

      const finalUrl = `${TCAppConfig.POST_INCOMING_DETAIL_V4}?oldProductArticleNo=${articleNo}&oldProductId=${currentProductId}&sameIncomingTCNewProductCreate=No`;

      const cleanPayloadForSeller = {
        tcId: TE_ID,
        productId: currentProductId,
        ...(isEditMode ? { incomingTcId: incomingTcId } : {}),
        incomingTcProductInformation: [
          {
            tcAssociatedProductArticleNo: articleNo,
            tcProductIncomingTcNo: 'na',
            isSellerFarm: 'Yes',
            incomingTcNoId: 'na',
            incomingTcLicensingCode: 'na',
            incomingTcProductNetWeightUsedInformation: [
              {
                tcProductIncomingArticleNo: 'na',
                tcProductIncomingArticleNetWeightUsed: 0,
                tcProductIncomingTcStandard: ['na'],
                tcProductIncomingRawMaterialInformation: [],
              },
            ],
          },
        ],
      };

      try {
        const result = await executePost(finalUrl, cleanPayloadForSeller);
        setLoadingState(false);
        if (result?.status === 200) {
          notify("Data Saved Successfully", "success");
          await onNextTab();
          setDrawerOpen(false);
          resetDrawerState();
          await loadIncomingTcDetails();
          return true;
        } else {
          notify("Unable to Save", "error");
          return false;
        }
      } catch (err: any) {
        console.error('Error saving incoming TC (seller):', err);
        setLoadingState(false);
        notify(err?.message || 'Failed to save incoming TC', 'error');
        return false;
      }
    }

    // Validate selected articles (tcIncomingProductNo is the array of selected incoming products)
    const selectedArticles = Array.isArray(stepData.incomingTcInfo.tcIncomingProductNo)
      ? stepData.incomingTcInfo.tcIncomingProductNo
      : [];

    if (selectedArticles.length === 0) {
      notify("Please select at least one Incoming Product Article Number", "error");
      return;
    }

    // Validate net weights for selected articles
    const netWeights = stepData.incomingProductNetWeight.netWeights || {};
    const missingWeights = selectedArticles.filter((art: string) => !netWeights[art] || Number(netWeights[art]) <= 0);
    if (missingWeights.length > 0) {
      notify(`Please enter net weight for selected products: ${missingWeights.join(", ")}`, "error");
      return;
    }

    // Validate against certified (max) weights if available
    const maxWeightsMap = (stepData.incomingProductNetWeight as any).maxWeights || {};
    const exceeding = selectedArticles.filter((art: string) => {
      const max = maxWeightsMap[art];
      const net = Number(netWeights[art]) || 0;
      // Only consider as exceeding when a valid certified max exists (> 0)
      return typeof max === 'number' && max > 0 && net > max;
    });
    if (exceeding.length > 0) {
      notify(`Product net weight must be less than or equal to the certified weight for: ${exceeding.join(", ")}`, "error");
      setLoadingState(false);
      return;
    }

    // Get the primary article (tcAssociatedProductArticleNo is the main product)
    const articleNo = stepData.incomingTcInfo.tcAssociatedProductArticleNo;
    if (!articleNo) {
      notify("Please select Associated Product Article Number", "error");
      return;
    }

    // Find shipment data for the selected article
    const shipment = getProductShipmentDataByArticle(productData, articleNo);
    if (!shipment) {
      notify("No shipment found for selected article", "error");
      return;
    }

    setLoadingState(true);

    // ========================================
    // SCENARIO DETECTION LOGIC
    // ========================================
    
    const isNewIncomingTC = !incomingTcId || incomingTcId === "";
    const currentProductId = shipment.productId;
    const currentArticleNo = stepData.incomingTcInfo.tcAssociatedProductArticleNo;
    
    // Check if product has changed from original
    const productChanged =
      originalArticleNo &&
      originalArticleNo !== currentArticleNo &&
      !isNewIncomingTC;

    let oldProductIdParam = currentProductId;
    let oldProductArticleNoParam = currentArticleNo;
    let sameIncomingTCNewProductCreate = "No";
    let scenario = "";

    // ========================================
    // DETERMINE SCENARIO
    // ========================================
    
    if (isNewIncomingTC) {
      // SCENARIO 1: New Incoming TC + New Product
      oldProductIdParam = currentProductId;
      oldProductArticleNoParam = currentArticleNo;
      sameIncomingTCNewProductCreate = "No";
      
    } else if (isCopy) {
      // SCENARIO 2: Old Incoming TC + New Product (Copy to different/same product)
      oldProductIdParam = currentProductId;
      oldProductArticleNoParam = currentArticleNo;
      sameIncomingTCNewProductCreate = "Yes";
      
    } else if (!productChanged) {
      // SCENARIO 3: Old Incoming TC + Old Product (Edit same incoming TC on same product)
      oldProductIdParam = oldProductId ?? currentProductId;
      oldProductArticleNoParam = originalArticleNo ?? currentArticleNo;
      sameIncomingTCNewProductCreate = "No";
      
    } else {
      // SCENARIO 4: Old Incoming TC + Different Product (Edit incoming TC and change product)
      oldProductIdParam = oldProductId ?? currentProductId;
      oldProductArticleNoParam = originalArticleNo ?? currentArticleNo;
      sameIncomingTCNewProductCreate = "No";
    }

    // ========================================
    // BUILD FINAL URL WITH QUERY PARAMETERS
    // ========================================
    
    const queryParams = new URLSearchParams({
      oldProductArticleNo: oldProductArticleNoParam,
      oldProductId: oldProductIdParam,
      sameIncomingTCNewProductCreate,
    });

    const finalUrl = `${TCAppConfig.POST_INCOMING_DETAIL_V4}?${queryParams.toString()}`;

    console.log("Final URL:", finalUrl);

    // ========================================
    // BUILD CLEAN PAYLOAD
    // ========================================
    
  const standardsMap = stepData.incomingTcStandard || {};
  const flattened = Object.values(standardsMap).reduce<string[]>(
    (acc, arr) => acc.concat(Array.isArray(arr) ? arr : []),
    []
  );
  const normalizedStandards = Array.from(new Set(flattened.map(String)));

  const standardsMapForArticles = stepData.incomingTcStandard || {};
  const rawMaterialsMap = stepData.incomingProductNetWeight.rawMaterials || {};

  const netWeightEntries = selectedArticles.map((articleNo: string) => {
    const netWeightUsed = Number(netWeights[articleNo]) || 0;
    const rawMaterialsForArticle = rawMaterialsMap?.[articleNo] || [];
    const perArticleStandards =
      Array.isArray(standardsMapForArticles[articleNo]) && standardsMapForArticles[articleNo].length > 0
        ? standardsMapForArticles[articleNo]
        : normalizedStandards;

    const rawMaterialPayload =
      rawMaterialsForArticle.length > 0
        ? rawMaterialsForArticle.map((rm) => {
            const percentage = Number(rm.rawMaterialPercentage) || 0;
            const calculatedWeight = (percentage / 100) * netWeightUsed;
            return {
              tcProductCategoryCode: rm.tcProductCategoryCode || "",
              tcProductDetailCode: rm.tcProductDetailCode || "",
              rawMaterialCode: rm.rawMaterialCode || "",
              rawMaterialWeight: Number(calculatedWeight.toFixed(2)),
              rawMaterialPercentage: percentage,
            };
          })
        : [
            {
              tcProductCategoryCode: "",
              tcProductDetailCode: "",
              rawMaterialCode: "",
              rawMaterialWeight: 0,
              rawMaterialPercentage: 0,
            },
          ];

    return {
      tcProductIncomingArticleNo: articleNo,
      tcProductIncomingArticleNetWeightUsed: netWeightUsed,
      tcProductIncomingTcStandard: perArticleStandards,
      tcProductIncomingRawMaterialInformation: rawMaterialPayload,
    };
  });

  const licensingRaw = (stepData.incomingTcInfo as any).incomingTcLicensingCode || (stepData.incomingTcInfo as any).licensingCode || "";
  let licensingCodeOnly = String(licensingRaw || "").trim();
  if (licensingCodeOnly && !licensingCodeOnly.startsWith("CB-")) {
    licensingCodeOnly = `CB-${licensingCodeOnly}`;
  }

    const cleanPayload = {
      tcId: TE_ID,
      productId: currentProductId,
      ...(isNewIncomingTC ? {} : { incomingTcId: incomingTcId }),
      incomingTcProductInformation: [
        {
          tcAssociatedProductArticleNo: stepData.incomingTcInfo.tcAssociatedProductArticleNo,
          tcProductIncomingTcNo: stepData.incomingTcInfo.tcIncomingTCNo || stepData.incomingTcInfo.tcIncomingATCNo || stepData.incomingTcInfo.tcIncomingRMDFNo,
          isSellerFarm: 'No',
          incomingTcLicensingCode: licensingCodeOnly,
          incomingTcNoId: stepData.incomingTcInfo.tcIncomingTcId || stepData.incomingTcInfo.tcIncomingAtcId || stepData.incomingTcInfo.tcIncomingLedgerTransactionId,
          incomingTcProductNetWeightUsedInformation: netWeightEntries,
        },
      ],
    };


    // ========================================
    // EXECUTE API CALL
    // ========================================
    
    try {
      const result = await executePost(finalUrl, cleanPayload);
      setLoadingState(false);

      if (result?.status === 200) {
        notify("Data Saved Successfully", "success");
        
        setDrawerOpen(false);
        resetDrawerState();
        await loadIncomingTcDetails();
        return true;
      } else {
        notify("Unable to Save", "error");
        setLoadingState(false);
        return false;
      }
    } catch (err: any) {
      console.error(`Error saving incoming TC in ${scenario}:`, err);
      setLoadingState(false);
      notify(err?.message || "Failed to save incoming TC", "error");
      return false;
    }
  };

const steps = [
  {
    label: "Incoming TC Information",
    content: (
      <IncomingTcInfo
        formRef={incomingTcRef}
        componentData={stepData.incomingTcInfo}
        setComponentData={(data: IncomingTCInformation) =>
          setStepData((prev) => ({ ...prev, incomingTcInfo: data }))
        }
      />
    )
  },
  {
    label: "Incoming Tc standard",
    content: (
      <IncomingTcStandard
        formRef={incomingTcStandardRef}
        componentData={stepData.incomingTcStandard}
        setComponentData={(data: { [key: string]: string[] }) =>
          setStepData((prev) => ({ ...prev, incomingTcStandard: data }))
        }
        selectedArticles={Array.isArray(stepData.incomingTcInfo.tcIncomingProductNo) ? stepData.incomingTcInfo.tcIncomingProductNo : []}
        availableStandards={normalizeStandards(
          Array.isArray(stepData.incomingTcInfo.tcIncomingTCStandard) 
            ? stepData.incomingTcInfo.tcIncomingTCStandard 
            : []
        )}
      />
    ),
  },
  {
    label: "Incoming Product Net Weight Used",
    content: (
      <ProductNetWeight
        formRef={productWeightRef}
        componentData={stepData.incomingProductNetWeight}
        setComponentData={(data: IncomingProductNetWeightInfo) =>
          setStepData((prev) => ({ ...prev, incomingProductNetWeight: data }))
        }
        selectedArticles={Array.isArray(stepData.incomingTcInfo.tcIncomingProductNo) ? stepData.incomingTcInfo.tcIncomingProductNo : []}
        incomingTcId={stepData.incomingTcInfo.tcIncomingTcId}
        incomingAtcId={stepData.incomingTcInfo.tcIncomingAtcId}
        incomingLedgerTransactionId={stepData.incomingTcInfo.tcIncomingLedgerTransactionId}
      />
    ),
  },
];

  const columns: MRT_ColumnDef<IncomingTcEntry>[] = [
    { accessorKey: "incomingTcInfo.tcAssociatedProductArticleNo", header: "Associated Product Article Number" },
    {
      accessorKey: "incomingTcInfo.tcIncomingTCNo",
      header: "Incoming TC/ATU/RMDF Number",
      Cell: ({ row }) => {
        const atc = row.original.incomingTcInfo?.tcIncomingATCNo || "";
        const tc = row.original.incomingTcInfo?.tcIncomingTCNo || "";
        const rmdf = row.original.incomingTcInfo?.tcIncomingRMDFNo || "";
        if (atc) return `${atc}`;
        if (tc) return `${tc}`;
        if (rmdf) return `${rmdf}`;
        return "N/A";
      },
    },
    {
      accessorKey: "incomingTcInfo.tcIncomingTCStandard",
      header: "Incoming TC Standard",
      Cell: ({ row }) =>{
        const standards = row.original?.incomingTcInfo?.tcIncomingTCStandard;

        if (Array.isArray(standards)) {
          // If array is empty, show "na"
          if (standards.length === 0) {
            return "na";
          }
          // Check if all elements are "na"
          const allNa = standards.every((s: any) => String(s).toLowerCase() === 'na');
          if (allNa) {
            return "na";
          }
          // Filter out "na" values and join the rest
          const filtered = standards.filter((s: any) => String(s).toLowerCase() !== 'na');
          return filtered.length > 0 ? filtered.join(", ") : "na";
        }
        
        return standards || "na";
      },
    },
  ];

  const isSeller = (stepData?.incomingTcInfo as any)?.isFlagSeller === 'Yes';
  const isRMDF =
    !isSeller &&
    !!(
      (stepData?.incomingTcInfo as any)?.tcIncomingRMDFNo &&
      (stepData?.incomingTcInfo as any)?.tcIncomingRMDFNo !== 'na'
    );

  // If RMDF is selected (and not seller), skip the standards step entirely
  const stepsToUse = isSeller ? [steps[0]] : isRMDF ? [steps[0], steps[2]] : steps;

  const actions: ActionButtonConfig[] = [];
  if (isSeller) {
    // Only show Save for seller flow
    actions.push({ label: 'Save', onClick: handleFinish, variant: 'contained', color: 'primary' });
  } else {
    if (activeStep !== 0) {
      actions.push({
        label: 'Back',
        onClick: () => setActiveStep((prev) => Math.max(prev - 1, 0)),
        variant: 'outlined',
        color: 'default',
        disabled: activeStep === 0,
      });
    }
    actions.push({
      label: activeStep === stepsToUse.length - 1 ? 'Save' : 'Next',
      onClick: activeStep === stepsToUse.length - 1 ? handleFinish : handleNext,
      variant: 'contained',
      color: 'primary',
    });
  }

  // If seller mode toggled on, ensure we're on first step
  useEffect(() => {
    if (isSeller) setActiveStep(0);
  }, [isSeller]);

  if (isLoading && tableData.length === 0) return <LoaderComponent />;

  return (
    <Box>
      {!(hasSubmitTc) && (
        <Box display="flex" justifyContent="flex-end" mb={3}>
          <StyledButton variant="contained" onClick={handleAdd}>
            Add Incoming TC Information
          </StyledButton>
        </Box>
      )}

      <CustomTable
        columns={columns}
        data={tableData}
        enableRowActions={!hasSubmitTc}
        enableTopToolbar={false}
        enableBottomToolbar={true}
        rowCount={rowCount}
        paginations={pagination}
        setPaginations={setPagination}
        renderDetailPanel={({ row }) => <RenderDetailPanel row={row} />}
        renderRowActions={({ row }) => (
          <ActionsMenu
            row={row}
            onEdit={handleEdit}
            // onCopy={handleCopy}
            onDelete={handleDelete}
          />
        )}
      />

      <ActionsModalPopup
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          resetDrawerState();
        }}
        title={ isEdit ? "Edit Incoming TC Information" : "Add Incoming TC Information"}
        actions={actions}
      >
        <CustomStepper
          steps={stepsToUse}
          activeStep={activeStep}
          onStepChange={setActiveStep}
        />
      </ActionsModalPopup>

      <AlertPopup
        open={dialogOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete this ${deleteRowData?.incomingTcInfo?.tcAssociatedProductArticleNo } Incoming TC?`}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Yes"
        cancelText="Cancel"
      />

      {!drawerOpen && (
        <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
          <NavLink to="/v4/tc-dashboard">
            <InnerButton>Home</InnerButton>
          </NavLink>
                <NavLink to={`/v4/tc/${formtype}/product-details/${TE_ID}`}>
                  <InnerButton>Back</InnerButton>
          </NavLink>
          {tableData?.length > 0 && (
                <InnerButton onClick={handleNavigate}>Next</InnerButton>
              )}
        </Box>
      )}
    </Box>
  );
};

export default IncomingTcDetails;